// LLM:METADATA
// :topology: [Domain:Observability | Role:TopologyRegistry | Pattern:Registry]
// :relates-to: required-by: "All Components", uses: "logger.js"
// :rationale: "Centralized topology ID registry ensures consistent semantic context across application"
// :contract: pre: "Registry initialized", post: "Topology IDs defined", invariant: "All topology IDs are unique"
// :decision_cache: "Chose centralized registry over distributed IDs for consistency [OBS-003]"
// LLM:END

/**
 * Topology ID Registry
 * Defines all component topology IDs for semantic logging
 */

// Module System Domain
export const TOPOLOGY_MODULE_ITEMS = "ModuleItems"
export const TOPOLOGY_MODULE_ITEMS_MULTIEDIT = "ModuleItemsMultiedit"
export const TOPOLOGY_MODULE_NEW_ITEM = "ModuleNewItem"
export const TOPOLOGY_USE_MODULE_ITEMS = "UseModuleItems"
export const TOPOLOGY_USE_MODULE_EDIT = "UseModuleEdit"
export const TOPOLOGY_USE_MODULE_EDIT_API = "UseModuleEditApi"
export const TOPOLOGY_USE_MODULE_EDIT_STATE = "UseModuleEditState"
export const TOPOLOGY_USE_MODULE_EDIT_TOOLS = "UseModuleEditTools"
export const TOPOLOGY_ITEMS_TABLE = "ItemsTable"
export const TOPOLOGY_ITEMS_FILTERS = "ItemsFilters"
export const TOPOLOGY_ITEMS_BULK_ACTIONS = "ItemsBulkActions"

// API & Data Layer Domain
export const TOPOLOGY_API = "Api"
export const TOPOLOGY_ERROR_HANDLER = "ErrorHandler"
export const TOPOLOGY_LOGGER = "Logger"
export const TOPOLOGY_PARAMETER_TRANSFORMER = "ParameterTransformer"
export const TOPOLOGY_RESPONSE_NORMALIZER = "ResponseNormalizer"

// State Management Domain
export const TOPOLOGY_STORE_INDEX = "StoreIndex"
export const TOPOLOGY_STORE_APP = "StoreApp"
export const TOPOLOGY_STORE_AUTH = "StoreAuth"
export const TOPOLOGY_STORE_DICT = "StoreDict"
export const TOPOLOGY_STORE_UI = "StoreUi"

// Authentication & User Management Domain
export const TOPOLOGY_LOGIN = "Login"
export const TOPOLOGY_USERS = "Users"
export const TOPOLOGY_USERS_ADD = "UsersAdd"
export const TOPOLOGY_USE_AUTH = "UseAuth"
export const TOPOLOGY_USE_USER_PERMISSIONS = "UseUserPermissions"
export const TOPOLOGY_USE_USERS = "UseUsers"
export const TOPOLOGY_USER_FORM = "UserForm"
export const TOPOLOGY_USERS_LIST = "UsersList"

// Utility Domain
export const TOPOLOGY_SANITIZE = "Sanitize"
export const TOPOLOGY_CRYPTO = "Crypto"
export const TOPOLOGY_SECURE_LOGGER = "SecureLogger"
export const TOPOLOGY_CSRF = "Csrf"
export const TOPOLOGY_DASHBOARD = "Dashboard"

/**
 * Get topology ID for a component
 * @param {string} componentName - Component name
 * @returns {string} Topology ID
 */
export function getTopologyId(componentName) {
  const topologyMap = {
    ModuleItems: TOPOLOGY_MODULE_ITEMS,
    ModuleItemsMultiedit: TOPOLOGY_MODULE_ITEMS_MULTIEDIT,
    ModuleNewItem: TOPOLOGY_MODULE_NEW_ITEM,
    UseModuleItems: TOPOLOGY_USE_MODULE_ITEMS,
    UseModuleEdit: TOPOLOGY_USE_MODULE_EDIT,
    UseModuleEditApi: TOPOLOGY_USE_MODULE_EDIT_API,
    UseModuleEditState: TOPOLOGY_USE_MODULE_EDIT_STATE,
    UseModuleEditTools: TOPOLOGY_USE_MODULE_EDIT_TOOLS,
    ItemsTable: TOPOLOGY_ITEMS_TABLE,
    ItemsFilters: TOPOLOGY_ITEMS_FILTERS,
    ItemsBulkActions: TOPOLOGY_ITEMS_BULK_ACTIONS,
    Api: TOPOLOGY_API,
    ErrorHandler: TOPOLOGY_ERROR_HANDLER,
    Logger: TOPOLOGY_LOGGER,
    ParameterTransformer: TOPOLOGY_PARAMETER_TRANSFORMER,
    ResponseNormalizer: TOPOLOGY_RESPONSE_NORMALIZER,
    StoreIndex: TOPOLOGY_STORE_INDEX,
    StoreApp: TOPOLOGY_STORE_APP,
    StoreAuth: TOPOLOGY_STORE_AUTH,
    StoreDict: TOPOLOGY_STORE_DICT,
    StoreUi: TOPOLOGY_STORE_UI,
    Login: TOPOLOGY_LOGIN,
    Users: TOPOLOGY_USERS,
    UsersAdd: TOPOLOGY_USERS_ADD,
    UseAuth: TOPOLOGY_USE_AUTH,
    UseUserPermissions: TOPOLOGY_USE_USER_PERMISSIONS,
    UseUsers: TOPOLOGY_USE_USERS,
    UserForm: TOPOLOGY_USER_FORM,
    UsersList: TOPOLOGY_USERS_LIST,
    Sanitize: TOPOLOGY_SANITIZE,
    Crypto: TOPOLOGY_CRYPTO,
    SecureLogger: TOPOLOGY_SECURE_LOGGER,
    Csrf: TOPOLOGY_CSRF,
    Dashboard: TOPOLOGY_DASHBOARD,
  }

  return topologyMap[componentName] || "UNKNOWN"
}
